import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ApiError,
  apiDelete,
  apiFetch,
  apiPatch,
  apiPost,
  apiPut,
  apiUpload,
  extractRequestId,
} from "@/lib/api";
import { fileKeys, taskKeys, taskTemplateKeys } from "@/lib/query";
import { toastError } from "@/lib/toast-error";

interface ProjectTask {
  id: string;
  projectId: string;
  title: string | null;
  description: string | null;
  taskType: string | null;
  phase: string | null;
  status: string | null;
  assigneeId: string | null;
  assignedToUserId: string | null;
  assignedToCompanyId: string | null;
  assignedToContactId: string | null;
  assignedTradeId: string | null;
  assignedRole: string | null;
  dueDate: string | null;
  priority: string | null;
  progress: number | null;
  displayOrder: number | null;
  sortOrder: number | null;
  evidenceRequired: boolean | null;
  evidenceDescription: string | null;
  defaultEvidenceFolderId: string | null;
}

interface TaskTemplate {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  projectType: string | null;
  folderTemplateId: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface TaskTemplateItem {
  id: string;
  templateId: string;
  name: string;
  sequence: number;
  offsetDays: number;
  tradeId: string | null;
  assignedRole: string | null;
  assignedContactGroupId: string | null;
  helpText: string | null;
  evidenceRequired: boolean;
  defaultEvidenceFolderPath: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TaskTemplateDetail extends TaskTemplate {
  items: TaskTemplateItem[];
}

interface TaskFile {
  id: string;
  fileName: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  version: number;
  isLatestVersion: boolean;
  folderId: string | null;
  folderPath: string | null;
  uploadedByUserId: string | null;
  uploadedByFirstName: string | null;
  uploadedByLastName: string | null;
  createdAt: string;
}

interface LinkedTask {
  id: string;
  title: string | null;
  projectId: string;
  status: string | null;
}

export type {
  LinkedTask,
  ProjectTask,
  TaskFile,
  TaskTemplate,
  TaskTemplateDetail,
  TaskTemplateItem,
};

export function useProjectTasks(projectId: string) {
  return useQuery({
    queryKey: taskKeys.project(projectId),
    queryFn: () => apiFetch<ProjectTask[]>(`/projects/${projectId}/tasks`),
    enabled: !!projectId,
  });
}

export interface MyTask {
  id: string;
  projectId: string;
  projectName: string | null;
  title: string | null;
  status: string | null;
  priority: string | null;
  dueDate: string | null;
  assignedToCompanyId: string | null;
  assignedToContactId: string | null;
}

export function useMyTasks(enabled: boolean) {
  return useQuery({
    queryKey: taskKeys.my(),
    queryFn: () => apiFetch<MyTask[]>("/my-tasks"),
    enabled,
  });
}

export function useCreateProjectTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ProjectTask>) =>
      apiPost<{ data: ProjectTask }>(`/projects/${projectId}/tasks`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      toast.success("Task created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateProjectTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...data }: { taskId: string } & Partial<ProjectTask>) =>
      apiPatch<{ data: ProjectTask }>(`/projects/${projectId}/tasks/${taskId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      toast.success("Task updated");
    },
    onError: (err: Error) => {
      if (err.message === "evidence_required") return;
      toast.error(err.message);
    },
  });
}

export function useDeleteProjectTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => apiDelete(`/projects/${projectId}/tasks/${taskId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      toast.success("Task deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useTaskFiles(projectId: string, taskId: string) {
  return useQuery({
    queryKey: taskKeys.files(projectId, taskId),
    queryFn: () => apiFetch<TaskFile[]>(`/projects/${projectId}/tasks/${taskId}/files`),
    enabled: !!projectId && !!taskId,
  });
}

export type EvidenceUploadFailure =
  | { kind: "folder_required" }
  | { kind: "folder_missing" }
  | { kind: "folder_forbidden" }
  | { kind: "generic"; message: string };

export function useUploadTaskEvidence(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { file: File; folderId?: string | null; description?: string }) => {
      const fd = new FormData();
      fd.append("file", args.file);
      if (args.folderId) fd.append("folderId", args.folderId);
      if (args.description) fd.append("description", args.description);
      return apiUpload<unknown>(`/projects/${projectId}/tasks/${taskId}/evidence`, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.files(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("File uploaded");
    },
    onError: (err: Error) => {
      const code = err.message;
      const ctx = { action: "upload-task-evidence", extra: { projectId, taskId } };
      if (code === "folder_required")
        toastError(new Error("Set an evidence folder on this task before uploading"), ctx);
      else if (code === "folder_missing")
        toastError(new Error("The selected evidence folder no longer exists"), ctx);
      else if (code === "folder_forbidden")
        toastError(new Error("You don't have permission to upload to that folder"), ctx);
      else toastError(err, ctx);
    },
  });
}

export function useLinkedTasksForFile(fileId: string) {
  return useQuery({
    queryKey: fileKeys.linkedTasks(fileId),
    queryFn: () => apiFetch<LinkedTask[]>(`/files/${fileId}/linked-tasks`),
    enabled: !!fileId,
  });
}

/** Task-scoped file delete — invalidates the task file list automatically. */
export function useDeleteTaskFile(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: string) => {
      const res = await fetch(`/api/files/${fileId}`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError({
          status: res.status,
          message: (body as { error?: string }).error ?? "Delete failed",
          body,
          requestId: extractRequestId(res, body),
          path: "(file delete)",
          method: "DELETE",
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.files(projectId, taskId) });
      qc.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("File deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteFileWithConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId, confirmed }: { fileId: string; confirmed?: boolean }) => {
      const res = await fetch(`/api/files/${fileId}${confirmed ? "?confirmed=true" : ""}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError({
          status: res.status,
          message: (body as { error?: string }).error ?? "Delete failed",
          body,
          requestId: extractRequestId(res, body),
          path: "(file delete)",
          method: "DELETE",
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fileKeys.all });
    },
  });
}

export function useCreateTasksFromTemplate(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { templateId: string }) =>
      apiPost<{ data: ProjectTask[] }>(`/projects/${projectId}/tasks/from-template`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      toast.success("Tasks created from template");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Task Templates (Admin) ──

export function useTaskTemplates() {
  return useQuery({
    queryKey: taskTemplateKeys.all,
    queryFn: () => apiFetch<TaskTemplate[]>("/task-templates"),
  });
}

export function useTaskTemplate(id: string) {
  return useQuery({
    queryKey: taskTemplateKeys.detail(id),
    queryFn: () => apiFetch<TaskTemplateDetail>(`/task-templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateTaskTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TaskTemplate>) =>
      apiPost<{ data: TaskTemplate }>("/task-templates", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskTemplateKeys.all });
      toast.success("Task template created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateTaskTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<TaskTemplate>) =>
      apiPut<{ data: TaskTemplate }>(`/task-templates/${id}`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: taskTemplateKeys.all });
      qc.invalidateQueries({ queryKey: taskTemplateKeys.detail(variables.id) });
      toast.success("Task template updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteTaskTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/task-templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskTemplateKeys.all });
      toast.success("Task template deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useCreateTaskTemplateItem(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TaskTemplateItem>) =>
      apiPost<{ data: TaskTemplateItem }>(`/task-templates/${templateId}/tasks`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskTemplateKeys.detail(templateId) });
      toast.success("Template item added");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateTaskTemplateItem(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...data }: { taskId: string } & Partial<TaskTemplateItem>) =>
      apiPut<{ data: TaskTemplateItem }>(`/task-templates/${templateId}/tasks/${taskId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskTemplateKeys.detail(templateId) });
      toast.success("Template item updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteTaskTemplateItem(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => apiDelete(`/task-templates/${templateId}/tasks/${taskId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskTemplateKeys.detail(templateId) });
      toast.success("Template item removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Task Collaboration (comments, activity, dependencies, checklist) ──

export interface TaskComment {
  id: string;
  taskId: string;
  authorUserId: string | null;
  authorName: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface TaskActivityEntry {
  id: string;
  taskId: string;
  actorUserId: string | null;
  actorName: string | null;
  kind: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface TaskDependency {
  id: string;
  taskId: string;
  dependsOnTaskId: string;
  dependsOnTitle: string | null;
  dependsOnStatus: string | null;
  isBlocker: boolean;
  createdAt: string;
}

export interface ChecklistItem {
  id: string;
  taskId: string;
  text: string;
  done: boolean;
  displayOrder: number;
  assignedToUserId: string | null;
  dueDate: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface TaskSummaryCounts {
  task_id: string;
  comment_count: number;
  dep_count: number;
  blocker_count: number;
  checklist_total: number;
  checklist_done: number;
}

export function useTaskSummaryCounts(projectId: string) {
  return useQuery({
    queryKey: ["tasks", projectId, "summary-counts"] as const,
    queryFn: async () => {
      const res = await apiFetch<{ data: TaskSummaryCounts[] }>(
        `/projects/${projectId}/tasks-summary-counts`,
      );
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useTaskComments(projectId: string, taskId: string) {
  return useQuery({
    queryKey: taskKeys.comments(projectId, taskId),
    queryFn: async () => {
      const res = await apiFetch<{ data: TaskComment[] }>(
        `/projects/${projectId}/tasks/${taskId}/comments`,
      );
      return res.data;
    },
    enabled: !!projectId && !!taskId,
  });
}

export function usePostTaskComment(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      apiPost(`/projects/${projectId}/tasks/${taskId}/comments`, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.comments(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.activity(projectId, taskId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useTaskActivity(projectId: string, taskId: string) {
  return useQuery({
    queryKey: taskKeys.activity(projectId, taskId),
    queryFn: async () => {
      const res = await apiFetch<{ data: TaskActivityEntry[] }>(
        `/projects/${projectId}/tasks/${taskId}/activity`,
      );
      return res.data;
    },
    enabled: !!projectId && !!taskId,
  });
}

export function useTaskDependencies(projectId: string, taskId: string) {
  return useQuery({
    queryKey: taskKeys.dependencies(projectId, taskId),
    queryFn: async () => {
      const res = await apiFetch<{ data: TaskDependency[] }>(
        `/projects/${projectId}/tasks/${taskId}/dependencies`,
      );
      return res.data;
    },
    enabled: !!projectId && !!taskId,
  });
}

export function useAddTaskDependency(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { dependsOnTaskId: string; isBlocker?: boolean }) =>
      apiPost(`/projects/${projectId}/tasks/${taskId}/dependencies`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.dependencies(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.activity(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      toast.success("Dependency linked");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRemoveTaskDependency(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (depId: string) =>
      apiDelete(`/projects/${projectId}/tasks/${taskId}/dependencies/${depId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.dependencies(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.activity(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      toast.success("Dependency removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useTaskChecklist(projectId: string, taskId: string) {
  return useQuery({
    queryKey: taskKeys.checklist(projectId, taskId),
    queryFn: async () => {
      const res = await apiFetch<{ data: ChecklistItem[] }>(
        `/projects/${projectId}/tasks/${taskId}/checklist`,
      );
      return res.data;
    },
    enabled: !!projectId && !!taskId,
  });
}

export function useAddChecklistItem(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      text: string;
      assignedToUserId?: string | null;
      dueDate?: string | null;
    }) => apiPost(`/projects/${projectId}/tasks/${taskId}/checklist`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.checklist(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useToggleChecklistItem(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      ...input
    }: {
      itemId: string;
      text?: string;
      done?: boolean;
      assignedToUserId?: string | null;
      dueDate?: string | null;
    }) => apiPatch(`/projects/${projectId}/tasks/${taskId}/checklist/${itemId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.checklist(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.activity(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRemoveChecklistItem(projectId: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apiDelete(`/projects/${projectId}/tasks/${taskId}/checklist/${itemId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.checklist(projectId, taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
